import { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { useCreatePermissionOverride } from '@/api/mutations/useCreatePermissionOverride';
import { useDeleteAllPermissionOverrides } from '@/api/mutations/useDeleteAllPermissionOverrides';
import { useDeletePermissionOverride } from '@/api/mutations/useDeletePermissionOverride';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useUpdatePermissionOverride } from '@/api/mutations/useUpdatePermissionOverride';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetPermissionOverrides } from '@/api/queries/useGetPermissionOverrides';
import { useGetRoles } from '@/api/queries/useGetRoles';
import { useGetUsersInOrg } from '@/api/queries/useGetUsersInOrg';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate, useHasPermission } from '@/hooks/useHasPermission';
import { UserTable } from '@/new/components/add-user-table';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { MatterUsersTable, buildMatterUserRows } from '@/new/components/matter-users-table';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';

const VIEW = {
  ManageUsers: 'manage-users',
  AddUsers: 'add-users',
} as const;

type View = keyof typeof VIEW;

export function Users() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const [activeView, setActiveView] = useState<View>('ManageUsers');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  const { user: currentUser } = useUserContext();
  const { hasPermission } = useHasPermission();
  const canAddUsers = hasPermission(Permission.MatterAddUsers);
  const canRemoveUsers = hasPermission(Permission.MatterRemoveUsers);
  const canManagePermissions = hasPermission(Permission.UserManage);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: orgUsers } = useGetUsersInOrg();
  const { data: roles } = useGetRoles();

  const matterUsers = matter?.users ?? [];

  const isCreator = currentUser?.id === matter?.createdById;
  const canManageOverrides = canManagePermissions || isCreator;

  const { data: overrides } = useGetPermissionOverrides(matterId, { enabled: canManageOverrides });

  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);
  const { mutate: createOverride } = useCreatePermissionOverride(matterId);
  const { mutate: updateOverride } = useUpdatePermissionOverride(matterId);
  const { mutate: deleteOverride } = useDeletePermissionOverride(matterId);
  const { mutate: deleteAllOverrides, isLoading: isDeletingAll } = useDeleteAllPermissionOverrides(matterId);
  const overridesEnabled = matter?.permissionOverridesEnabled ?? false;
  const hasOverrides = (overrides?.length ?? 0) > 0;
  const canModifyUsers = canAddUsers || canRemoveUsers || canManageOverrides;

  const handleToggleOverrides = useCallback(
    async (enabled: boolean) => {
      try {
        await updateMatter({ permissionOverridesEnabled: enabled });
        toast.success(`Permission overrides ${enabled ? 'enabled' : 'disabled'}`);
      } catch {
        toast.error('Failed to update permission overrides');
      }
    },
    [updateMatter],
  );

  const handleUserChange = useCallback(
    async (users: User[]) => {
      try {
        await updateMatter({ userIds: users.map((x) => x.id) });
        toast.success('Users updated');
      } catch {
        toast.error('Failed to update users');
      }
    },
    [updateMatter],
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      handleUserChange(matterUsers.filter((u) => u.id !== userId));
    },
    [matterUsers, handleUserChange],
  );

  const handleRoleOverrideChange = useCallback(
    (userId: string, roleId: string) => {
      const existing = overrides?.find((o) => o.userId === userId);
      if (existing) {
        if (roleId === 'none') {
          deleteOverride({ overrideId: existing.id });
        } else {
          updateOverride({ overrideId: existing.id, roleId });
        }
      } else if (roleId !== 'none') {
        createOverride({ userId, roleId });
      }
    },
    [overrides, createOverride, updateOverride, deleteOverride],
  );

  const showManageUsersView = useCallback(() => setActiveView('ManageUsers'), []);
  const showAddUsersView = useCallback(() => setActiveView('AddUsers'), []);

  const orgUserMap = useMemo(() => {
    const map = new Map<string, User>();
    orgUsers?.forEach((u) => map.set(u.id, u));
    return map;
  }, [orgUsers]);

  const overrideMap = useMemo(() => {
    const map = new Map<string, { id: string; roleId: string; roleName: string }>();
    overrides?.forEach((o) => map.set(o.userId, { id: o.id, roleId: o.roleId, roleName: o.roleName }));
    return map;
  }, [overrides]);

  const matterUserRows = useMemo(
    () => buildMatterUserRows(matterUsers, orgUserMap, overrideMap, currentUser?.id, matter?.createdById),
    [matterUsers, orgUserMap, overrideMap, currentUser?.id, matter?.createdById],
  );

  return (
    <>
      <SettingsHeader
        title="Users"
        subheading={
          <p className="text-sm text-gray-500">
            {canModifyUsers
              ? 'View and manage the users who have access to this matter.'
              : 'View the users who have access to this matter.'}
          </p>
        }
      />

      <div className="mb-4 flex items-center justify-between">
        <ButtonGroup>
          <Button
            className={`py-1.5 ${activeView === 'ManageUsers' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
            onClick={showManageUsersView}
          >
            <Icon
              className={`lucide--users-round h-4 w-4 ${activeView === 'ManageUsers' ? 'text-gray-700' : 'text-gray-500'}`}
            />
            Manage Users
          </Button>
          <Button
            className={`py-1.5 ${activeView === 'AddUsers' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
            onClick={showAddUsersView}
          >
            <Icon
              className={`lucide--user-plus h-4 w-4 ${activeView === 'AddUsers' ? 'text-gray-700' : 'text-gray-500'}`}
            />
            Add Users
          </Button>
        </ButtonGroup>
      </div>

      {activeView === 'ManageUsers' && (
        <div className="space-y-4">
          {canManageOverrides && (
            <div className="flex items-center gap-5 rounded-lg border border-gray-200 bg-white p-5">
              <Switch id="permission-overrides" checked={overridesEnabled} onCheckedChange={handleToggleOverrides} />
              <div>
                <Label htmlFor="permission-overrides" className="mb-1 text-sm font-medium text-gray-900">
                  Matter-level permission overrides
                </Label>
                <p className="text-xs text-gray-500">
                  Override user roles for this matter. Users will have different permissions within this matter than
                  their global role. This only applies to Matter-level permissions.
                </p>
              </div>
            </div>
          )}

          <PermissionGate
            permission={Permission.MatterViewUsers}
            orAllowed={canManagePermissions}
            uiMode="disabled"
            disabledMessage="You do not have permission to view users in this matter"
          >
            <MatterUsersTable
              matterUsers={matterUserRows}
              overridesEnabled={overridesEnabled}
              canManageOverrides={canManageOverrides}
              canRemoveUsers={canRemoveUsers}
              roles={roles ?? []}
              isGlobalAdmin={currentUser?.organisation?.isGlobalAdmin ?? false}
              additionalButtons={
                canManageOverrides && overridesEnabled ? (
                  <>
                    <Button
                      className="ml-auto"
                      disabled={!hasOverrides || isDeletingAll}
                      onClick={() => setIsResetConfirmOpen(true)}
                    >
                      <Icon className="lucide--rotate-ccw" />
                      Reset All Overrides
                    </Button>
                    <ConfirmationModal
                      isOpen={isResetConfirmOpen}
                      onOpenChange={setIsResetConfirmOpen}
                      title="Reset All Overrides"
                      onConfirm={() => {
                        deleteAllOverrides();
                        setIsResetConfirmOpen(false);
                      }}
                      isLoading={isDeletingAll}
                    >
                      <p className="text-sm text-gray-600">
                        Are you sure you want to remove all Matter-level permission overrides? All users will revert to
                        their global roles.
                      </p>
                    </ConfirmationModal>
                  </>
                ) : undefined
              }
              onRoleOverrideChange={handleRoleOverrideChange}
              onRemoveUser={handleRemoveUser}
            />
          </PermissionGate>
        </div>
      )}

      {activeView === 'AddUsers' &&
        (canAddUsers ? (
          <UserTable
            users={orgUsers ?? []}
            matterCreatorId={matter?.createdById}
            addedUsers={matterUsers}
            onUserChange={handleUserChange}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              You do not have permission to add users. Contact an administrator to request access.
            </p>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Users with access ({matterUsers.length})</h3>
              <div className="space-y-2">
                {matterUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="lowercase">{user.email}</span>
                    {user.id === matter?.createdById && <span className="text-xs text-gray-500">(Creator)</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
    </>
  );
}
